// src/components/partial-pages/GeneralSettingsPage/VoiceSettingsSubsection.tsx
/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import { useEffect, useMemo, useState, type ChangeEventHandler } from 'react'
import {
  Button,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControlLabel,
  Switch,
} from '@material-ui/core'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import { StyledInput, NumberBubble, StyledInputLabel } from 'components/widgets'
import { PRIMARY_COLOR, getAppColors } from 'lib/constants'
import type { Organization, VoiceBackend } from 'lib/types'
import { useGlobal } from 'contexts/GlobalProvider'
import { Users } from 'models'
import ZelloSetupWizardModal from 'components/partials/ZelloSetupWizardModal/ZelloSetupWizardModal'
import { isVoiceEnabled } from 'lib/utils/auth'
import { fleetVoiceFlag } from 'lib/utils/featureFlags'
import { disableFleetVoiceForOrg, enableFleetVoiceForOrg } from 'models/api/voice'
import { ConfirmationDialog } from 'components/partials/ConfirmationDialog'

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  maxWidth: 520,
})

/** Shared label style so both headers match */
const FieldLabel = styled.div({
  fontFamily: 'Montserrat',
  fontSize: '1.0rem',
  fontStyle: 'normal',
  fontWeight: 'bold',
  lineHeight: '1.2rem',
  letterSpacing: '0.02rem',
  color: getAppColors('--color-text'),
})

/** ✅ Shared header row: same minHeight so left/right align */
const HeaderRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 24, // force same header height across columns
})

const InfoTrigger = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  cursor: 'pointer',
  transform: 'translateY(-1px)',
  color: PRIMARY_COLOR,
  opacity: 0.8,
})

/**
 * ✅ Input width to match the label length visually.
 * (The label "Allowed voice users" is roughly ~200-220px wide in your screenshot)
 */
const INPUT_WIDTH = 160

const Input = styled(StyledInput)({
  width: `${INPUT_WIDTH}px`,
  marginTop: 8,

  // ✅ force the control to be shorter
  height: 26,
  minHeight: 26,

  // ✅ ensure the MUI root/container uses that height
  '& .MuiInputBase-root': {
    height: '26px',
    minHeight: '26px',
    alignItems: 'center',
  },

  // ✅ tighten the actual input text box
  '& .MuiInputBase-input': {
    paddingTop: 0,
    paddingBottom: 0,
    height: '26px',
    lineHeight: '26px', // centers the digits vertically
    fontSize: '12px',
    boxSizing: 'border-box',
  },

  // (optional) if you're using underline variant, kill extra spacing
  '& .MuiInput-underline:before, & .MuiInput-underline:after': {
    bottom: 0,
  },
})

const ActionButton = styled(Button)({
  color: PRIMARY_COLOR,
  padding: '8px 20px',
  border: `2px solid ${PRIMARY_COLOR}`,
  boxSizing: 'border-box',
  fontFamily: 'Montserrat',
  boxShadow: '0px 4px 8px rgba(20, 48, 72, 0.1)',
  fontSize: '1.0rem',
  fontStyle: 'normal',
  fontWeight: 'bold',
  lineHeight: '1.2rem',
  letterSpacing: '0.1rem',
  textAlign: 'left',
  marginRight: '5px',
  cursor: 'pointer',
  borderRadius: '2px',

  '&.Mui-disabled': {
    opacity: 0.5,
    boxShadow: 'none',
    cursor: 'not-allowed',
  },
})

const SwitchLabel = styled.div({
  fontSize: '1.0rem',
})

const Toggles = styled.div({
  display: 'flex',
  marginTop: 10,
  marginBottom: 6,
})

const Toggle = styled.div({
  flex: 1,
})

const ToggleSwitch = withStyles({
  switchBase: {
    color: 'white',
    '&$checked': { color: 'rgba(48, 188, 176, 1)' },
    '&$checked + $track': { backgroundColor: 'rgba(48, 188, 176, 0.33)' },
  },
  checked: {},
  track: {},
})(Switch)

const BubbleWrap = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
})

/** ✅ Layout row */
const Row2Col = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 28,
  marginTop: 12,
  flexWrap: 'nowrap',
})

/** ✅ Keep both columns same “block” width */
const LeftCol = styled.div({
  width: `${INPUT_WIDTH}px`,
})

const RightCol = styled.div({
  width: `${INPUT_WIDTH}px`,
})

/**
 * ✅ Bubble aligned to input row:
 * - same marginTop as Input
 * - same height band as the input container area
 * - alignItems center so bubble centers vertically vs input text region
 */
const AlignRow = styled.div({
  marginTop: 4, // same as Input marginTop
  height: 34, // matches the visual height of the input band (tweak if needed)
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start', // bubble left under label
})

const useTooltipStyles = makeStyles(() => ({
  tooltip: {
    background: 'transparent',
    padding: 0,
    boxShadow: 'none',
    maxWidth: 'unset',
  },
  arrow: {
    color: 'transparent',
    '&::before': { border: 0 },
  },
}))

function LicensesPopoverContent({ emails }: { emails: string[] }) {
  return (
    <Paper
      elevation={3}
      style={{
        background: getAppColors('--color-surface'),
        borderRadius: 8,
        padding: 8,
        maxWidth: 420,
        maxHeight: 320,
        overflowY: 'auto',
        boxShadow: '0px 6px 18px rgba(0,0,0,0.12)',
        fontFamily: 'Montserrat',
      }}
    >
      {emails.length === 0 ? (
        <div style={{ padding: 8, fontWeight: 600, color: getAppColors('--color-text') }}>No assigned licenses</div>
      ) : (
        <Table size="small" aria-label="assigned-licenses">
          <TableHead>
            <TableRow>
              <TableCell
                style={{
                  fontWeight: 700,
                  color: getAppColors('--color-text'),
                  borderBottom: `1px solid ${getAppColors('--color-gray-4')}`,
                }}
              >
                Email
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emails.map(email => (
              <TableRow key={email} hover>
                <TableCell
                  style={{ borderBottom: `1px solid ${getAppColors('--color-gray-4')}`, wordBreak: 'break-all' }}
                >
                  {email}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  )
}

function AssignedLicensesBubble({ emails, slots }: { emails: string[]; slots: number | string }) {
  const tooltipClasses = useTooltipStyles()
  const count = emails.length

  return (
    <Tooltip
      classes={tooltipClasses}
      title={<LicensesPopoverContent emails={emails} />}
      placement="top"
      interactive
      enterDelay={150}
      arrow
      PopperProps={{ modifiers: { offset: { enabled: true, offset: '0,8' } } }}
    >
      <BubbleWrap>
        <NumberBubble value={`${count} / ${slots}`} />
      </BubbleWrap>
    </Tooltip>
  )
}

// Greys out and blocks a group of controls: the voice options below an
// Essential tier, and the Zello ones once the org is on the voice service.
const Greyable = styled.div<{ inactive: boolean }>(({ inactive }) => ({
  opacity: inactive ? 0.45 : 1,
  pointerEvents: inactive ? 'none' : 'auto',
}))

type Props = {
  org: Organization | null
  // From the org response: `unflattenOrg` builds the update payload and leaves
  // these out, since only enable_voice sets them.
  voiceBackend?: VoiceBackend
  zelloProvisioningStatus?: string | null
  zelloUserMaxValue: number | ''
  onChangeZelloUserMax: (value: number | '') => void
  onOrgUpdated?: (org: Organization) => void
}

export function VoiceSettingsSubsection({
  org,
  voiceBackend,
  zelloProvisioningStatus,
  zelloUserMaxValue,
  onChangeZelloUserMax,
  onOrgUpdated,
}: Props) {
  const { licenses, licensesLoading, licensesError, selectedOrg } = useGlobal()
  const usersById = Users.use(({ usersById }) => usersById)
  // Users store is no longer eagerly loaded; hydrate just the license holders we need to display.
  useEffect(() => {
    const userIds = Array.from(new Set(((licenses || []) as any[]).map(l => l?.userId).filter(Boolean) as string[]))
    userIds.forEach(uid => {
      if (!usersById[uid]) Users.reloadUser(uid)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenses])
  const isZelloConfigured = useMemo(() => {
    const sub = (org as any)?.zelloSubdomain
    return sub !== null && sub !== undefined && String(sub).trim().length > 0
  }, [org])

  const isFleet = fleetVoiceFlag && voiceBackend === 'fleet'

  // The API offers fleet only to an org nothing has been committed for yet: a
  // Zello subdomain or any provisioning attempt rules it out. A fleet org can
  // always be turned off again.
  const zelloStarted = isZelloConfigured || Boolean(zelloProvisioningStatus)
  const fleetToggleLocked = !isFleet && zelloStarted

  // `org` is rebuilt from the form state, which leaves out `voiceBackend`.
  const isVoiceToggleEnabled = useMemo(
    () => Boolean(selectedOrg && isVoiceEnabled({ ...org, voiceBackend })),
    [org, voiceBackend, selectedOrg],
  )

  const voiceControlsDisabled = !isZelloConfigured && !isFleet

  // Follows the tier picked in the form, so choosing Elite unlocks these before
  // Apply Changes saves it.
  const tierAllowsVoice = org?.subscriptionTier === 'elite' || org?.subscriptionTier === 'ultimate'

  type FleetChange = 'enable' | 'disable'
  const [confirmFleet, setConfirmFleet] = useState<FleetChange | null>(null)
  const [savingFleet, setSavingFleet] = useState<FleetChange | null>(null)
  const [fleetError, setFleetError] = useState('')

  useEffect(() => {
    setFleetError('')
  }, [org?.id])

  const handleConfirmFleet = async () => {
    const change = confirmFleet
    if (!org?.id || !change || savingFleet) return
    setConfirmFleet(null)
    setSavingFleet(change)
    setFleetError('')
    try {
      const updated = change === 'enable' ? await enableFleetVoiceForOrg(org.id) : await disableFleetVoiceForOrg(org.id)
      onOrgUpdated?.(updated)
    } catch (e: any) {
      setFleetError(
        e?.response?.data?.errors?.[0]?.detail ||
          e?.response?.data?.errors?.[0]?.title ||
          e?.message ||
          `Unable to ${change} React Mobile Voice Service.`,
      )
    } finally {
      setSavingFleet(null)
    }
  }

  const [openZelloWizard, setOpenZelloWizard] = useState(false)

  const assignedLicenseEmails = useMemo<string[]>(() => {
    const assigned = (licenses || []).filter(l => Boolean((l as any)?.userId))
    return assigned
      .map(l => {
        const uid = String((l as any).userId)
        const u = (usersById as any)?.[uid]
        const email = u?.attributes?.email || u?.email
        return (email || uid || 'unknown').toString()
      })
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
  }, [licenses, usersById])

  const assignedCount = assignedLicenseEmails.length
  const [errorMsg, setErrorMsg] = useState<string>('')

  useEffect(() => {
    setErrorMsg('')
  }, [org?.id])

  const handleChange: ChangeEventHandler<HTMLInputElement> = e => {
    const v = e.target.value

    if (v === '') {
      onChangeZelloUserMax('')
      setErrorMsg('')
      return
    }

    const n = Number(v)
    if (!Number.isNaN(n) && n >= 0) {
      onChangeZelloUserMax(n)
      if (n < assignedCount) {
        setErrorMsg('Must enter a number less than assigned licenses or remove licenses and try again.')
      } else {
        setErrorMsg('')
      }
    }
  }

  const tooltipText =
    'This sets the amount of voice user emails allowed to talk from the portal or on mobile devices. A specific user (ex: JohnDoe@gmail.com) can talk on one device at a time. Subsequent active logins by the same user will disconnect the original login from the voice channel.'

  const fleetTooltipText =
    "Runs this org's voice on React Mobile's own voice service instead of Zello. It can only be turned on before Zello is set up. Turning it off ends voice for the org but keeps its channels and licenses."

  const belowAssigned = typeof zelloUserMaxValue === 'number' && zelloUserMaxValue < assignedCount

  // ✅ NEW: don’t show “below assigned” errors unless voice is actually enabled/configured
  const showBelowAssignedError = (isZelloConfigured || isFleet) && belowAssigned

  return (
    <Wrapper>
      <StyledInputLabel>Voice</StyledInputLabel>

      {org ? (
        <>
          <div style={{ marginTop: 10, marginBottom: 6 }}>
            <HeaderRow>
              <div
                style={{
                  fontFamily: 'Montserrat',
                  fontSize: '1.0rem',
                  fontWeight: 'bold',
                  color: isVoiceToggleEnabled ? 'rgba(48, 188, 176, 1)' : getAppColors('--color-secondary'),
                }}
              >
                {isVoiceToggleEnabled ? 'Enabled' : 'Disabled'}
              </div>

              <Tooltip
                title={
                  <span>
                    Voice is enabled when the organizations has subscription tier of Elite or Ultimate, the subscription
                    status is Paid or Delinquent, and either Zello is setup or React Mobile Voice Service is on. Voice
                    is disabled when the subscription tier is Essential or the subscription status is Suspended.
                  </span>
                }
                placement="right"
                arrow
              >
                <InfoTrigger role="button" tabIndex={0} aria-label="Voice status requirements">
                  <InfoOutlinedIcon fontSize="small" style={{ display: 'block', color: 'inherit', width: '20px' }} />
                </InfoTrigger>
              </Tooltip>
            </HeaderRow>
          </div>

          {!tierAllowsVoice ? (
            <div style={{ marginTop: 2, fontSize: 12, opacity: 0.85, maxWidth: 360 }}>
              Voice requires an Elite or Ultimate subscription tier.
            </div>
          ) : null}

          <Greyable inactive={!tierAllowsVoice}>
            {/* Hint to run the wizard first (matches old UX intent) */}
            {tierAllowsVoice && !isZelloConfigured && !isFleet ? (
              <div style={{ marginTop: 2, fontSize: 12, opacity: 0.85, maxWidth: 360 }}>
                {fleetVoiceFlag
                  ? 'Turn on React Mobile Voice Service or launch the Zello setup wizard to enable voice for this org.'
                  : 'Launch the setup wizard to enable voice for this org.'}
              </div>
            ) : null}

            {/* ✅ Voice controls gated behind zello setup */}
            <div
              style={{
                opacity: voiceControlsDisabled ? 0.45 : 1,
                pointerEvents: voiceControlsDisabled ? 'none' : 'auto',
              }}
            >
              <Row2Col>
                <LeftCol>
                  <HeaderRow>
                    <FieldLabel>Allowed voice users</FieldLabel>
                    <Tooltip title={<span>{tooltipText}</span>} placement="right" arrow>
                      <InfoTrigger role="button" tabIndex={0} aria-label="Allowed voice users info">
                        <InfoOutlinedIcon
                          fontSize="small"
                          style={{ display: 'block', color: 'inherit', width: '20px' }}
                        />
                      </InfoTrigger>
                    </Tooltip>
                  </HeaderRow>

                  <Input
                    disableUnderline
                    type="number"
                    inputProps={{ min: 0, step: 1, max: 999 }}
                    value={zelloUserMaxValue}
                    onChange={handleChange}
                    placeholder=""
                    style={{ margin: 0 }}
                  />
                </LeftCol>

                <RightCol>
                  <HeaderRow>
                    <FieldLabel>Assigned licenses</FieldLabel>
                  </HeaderRow>

                  <AlignRow>
                    {licensesLoading ? (
                      <div style={{ fontSize: '12px', opacity: 0.7 }}>Loading…</div>
                    ) : (
                      <AssignedLicensesBubble emails={assignedLicenseEmails} slots={zelloUserMaxValue} />
                    )}
                  </AlignRow>

                  {licensesError && <div style={{ color: 'red', marginTop: 6 }}>{licensesError}</div>}
                </RightCol>
              </Row2Col>
            </div>

            {/* Validation (only when enabled) */}
            {(errorMsg || showBelowAssignedError) && (
              <div style={{ color: 'red', marginTop: 10 }}>
                {errorMsg || 'Must enter a number less than assigned licenses or remove licenses and try again.'}
              </div>
            )}

            {fleetVoiceFlag && (
              <div style={{ marginTop: 14 }}>
                <HeaderRow>
                  <FieldLabel>React Mobile Voice Service</FieldLabel>
                  <Tooltip title={<span>{fleetTooltipText}</span>} placement="right" arrow>
                    <InfoTrigger role="button" tabIndex={0} aria-label="React Mobile Voice Service info">
                      <InfoOutlinedIcon
                        fontSize="small"
                        style={{ display: 'block', color: 'inherit', width: '20px' }}
                      />
                    </InfoTrigger>
                  </Tooltip>
                </HeaderRow>
                <FormControlLabel
                  style={{ marginLeft: 0 }}
                  control={
                    <ToggleSwitch
                      checked={isFleet}
                      disabled={fleetToggleLocked || Boolean(savingFleet)}
                      onChange={(_, checked) => setConfirmFleet(checked ? 'enable' : 'disable')}
                      name="fleetVoice"
                    />
                  }
                  label={
                    <SwitchLabel>
                      {savingFleet === 'enable'
                        ? 'Enabling…'
                        : savingFleet === 'disable'
                        ? 'Disabling…'
                        : isFleet
                        ? 'Enabled'
                        : 'Disabled'}
                    </SwitchLabel>
                  }
                />
                {!isFleet && zelloStarted ? (
                  <div style={{ fontSize: 12, opacity: 0.85, maxWidth: 360 }}>
                    Unavailable: Zello has already been set up for this org.
                  </div>
                ) : null}
                {fleetError && <div style={{ color: 'red', marginTop: 6, fontSize: 12 }}>{fleetError}</div>}
              </div>
            )}

            <Greyable inactive={isFleet}>
              <HeaderRow style={{ marginTop: 12 }}>
                <FieldLabel>Zello subdomain</FieldLabel>
              </HeaderRow>

              <Input
                disableUnderline
                readOnly
                value={(org as any)?.zelloSubdomain ?? ''}
                inputProps={{ 'aria-label': 'Zello subdomain' }}
                style={{ margin: 0 }}
              />

              {/* Wizard CTA (disabled once configured) */}
              <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <ActionButton
                  variant="outlined"
                  onClick={() => setOpenZelloWizard(true)}
                  disabled={isZelloConfigured || isFleet}
                  style={{ width: 'fit-content' }}
                >
                  🚀 Launch Zello Setup Wizard
                </ActionButton>

                {isZelloConfigured ? (
                  <div style={{ fontSize: 12, opacity: 0.85, maxWidth: 280 }}>Zello already setup for this org.</div>
                ) : null}
              </div>
            </Greyable>
          </Greyable>

          <ConfirmationDialog
            open={Boolean(confirmFleet)}
            title={`${confirmFleet === 'disable' ? 'Disable' : 'Enable'} React Mobile Voice Service`}
            description={
              confirmFleet === 'disable'
                ? "This org's devices and users lose voice within the hour. Its channels and licenses are kept for when the service is turned back on."
                : "This org's voice will run on the React Mobile Voice Service instead of Zello."
            }
            onConfirm={handleConfirmFleet}
            handleClose={() => setConfirmFleet(null)}
          />

          <ZelloSetupWizardModal
            open={openZelloWizard}
            onClose={() => setOpenZelloWizard(false)}
            org={org}
            disabledBecauseConfigured={isZelloConfigured}
            onOrgUpdated={(updated: Organization) => {
              onOrgUpdated?.(updated)
            }}
          />
        </>
      ) : null}
    </Wrapper>
  )
}
